import React, { useState, useEffect } from 'react';
import Modal from './Modal';

const T = {
  bg: "#2C2E33", txt: "#FAFAFA", tx3: "#9CA3AF", blue: "#4059AD",
  bdr: "rgba(217,219,221,0.12)", sf: "rgba(255,255,255,0.06)",
};

export default function FieldEditor({ label, value, type = "text", options = [], onSave, readOnly, suffix, prefix, renderDisplay }) {
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState(value ?? "");

  // Normalize select options so callers can pass either string[] or {value,label}[].
  // Downstream code iterates over normalizedOptions using .value/.label uniformly.
  const normalizedOptions = Array.isArray(options)
    ? options.map(o => (typeof o === "object" && o !== null && "value" in o) ? o : { value: o, label: o })
    : [];

  useEffect(() => { setVal(value ?? ""); }, [value]);

  const handleOpen = () => {
    if (readOnly) return;
    setVal(value ?? "");
    setOpen(true);
  };

  // Modal handles the fade-out timing internally — we just flip open to false.
  const handleClose = () => setOpen(false);

  const handleSave = () => {
    const finalVal = (type === "number" || type === "currency") ? Number(val) || 0 : val;
    onSave?.(finalVal);
    handleClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && type !== "textarea") handleSave();
    // Esc handled by Modal
  };

  const displayValue = (() => {
    if (!value && value !== 0) return "—";
    if (type === "currency") return (prefix || "$") + Number(value).toLocaleString();
    if (type === "select") {
      const found = normalizedOptions.find(o => o.value === value);
      return found ? found.label : String(value);
    }
    if (suffix) return value + suffix;
    return String(value);
  })();

  const inputStyle = {
    width: "100%", padding: "11px 14px", borderRadius: 10,
    border: "1.5px solid rgba(64,89,173,0.4)", background: "rgba(255,255,255,0.04)",
    color: T.txt, fontSize: 14, outline: "none", fontFamily: "inherit",
    transition: "border-color .2s, box-shadow .2s",
  };

  // Input-focus styles are FieldEditor-specific (only inputs with className
  // "fe-input" should match). Kept here rather than moved to Modal.
  const inputFocusStyle = `
    .fe-input:focus { border-color: #4059AD !important; box-shadow: 0 0 0 3px rgba(64,89,173,0.15) !important; }
    .fe-input::placeholder { color: rgba(250,250,250,0.3) !important; }
    input[type="date"].fe-input { color-scheme: dark; }
  `;

  // Picker mode (select) saves on click — no Save/Cancel footer.
  const isPicker = type === "select";

  const footer = isPicker ? null : (
    <>
      <button
        onClick={handleClose}
        style={{
          padding: "8px 18px", borderRadius: 10, border: "1px solid " + T.bdr,
          background: "transparent", color: T.tx3, fontSize: 12, fontWeight: 600,
          cursor: "pointer", fontFamily: "inherit", transition: "all .12s",
        }}
        onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.04)"}
        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
      >Cancel</button>
      <button
        onClick={handleSave}
        style={{
          padding: "8px 18px", borderRadius: 10, border: "none",
          background: T.blue, color: "#FAFAFA", fontSize: 12, fontWeight: 600,
          cursor: "pointer", fontFamily: "inherit",
          boxShadow: "0 2px 12px rgba(64,89,173,0.30)",
          transition: "all .12s",
        }}
        onMouseEnter={e => e.currentTarget.style.transform = "translateY(-1px)"}
        onMouseLeave={e => e.currentTarget.style.transform = "none"}
      >Save</button>
    </>
  );

  return (
    <>
      {renderDisplay ? (
        <div onClick={handleOpen} style={{ display: "inline-block" }}>
          {renderDisplay(value)}
        </div>
      ) : (
        <div
          onClick={handleOpen}
          style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            padding: "7px 0", borderBottom: "1px solid " + T.bdr,
            cursor: readOnly ? "default" : "pointer", minHeight: 32,
            transition: "background .12s",
          }}
          onMouseEnter={e => { if (!readOnly) e.currentTarget.style.background = "rgba(255,255,255,0.02)"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
        >
          <span style={{ fontSize: 11, color: T.tx3, minWidth: 110, flexShrink: 0 }}>{label}</span>
          <span style={{ fontSize: 11, color: value ? T.txt : T.tx3, textAlign: "right", flex: 1 }}>
            {displayValue}
          </span>
        </div>
      )}

      <Modal open={open} title={label} width={320} onClose={handleClose} footer={footer}>
        <style>{inputFocusStyle}</style>

        {/* Body branches by type — picker saves on click, others use the footer */}
        {type === "select" ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 240, overflowY: "auto" }}>
            {normalizedOptions.map(opt => (
              <button
                key={opt.value}
                onClick={() => { onSave?.(opt.value); handleClose(); }}
                style={{
                  padding: "10px 14px", borderRadius: 10, border: "none",
                  background: val === opt.value ? T.blue : "rgba(255,255,255,0.04)",
                  color: val === opt.value ? "#fff" : T.txt,
                  fontSize: 13, fontWeight: val === opt.value ? 600 : 400,
                  cursor: "pointer", textAlign: "left", fontFamily: "inherit",
                  transition: "all .15s cubic-bezier(.16,1,.3,1)",
                }}
                onMouseEnter={e => { if (val !== opt.value) e.currentTarget.style.background = "rgba(255,255,255,0.08)"; }}
                onMouseLeave={e => { if (val !== opt.value) e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        ) : type === "textarea" ? (
          <textarea
            className="fe-input"
            autoFocus value={val} onChange={e => setVal(e.target.value)} onKeyDown={handleKeyDown}
            rows={3}
            style={{ ...inputStyle, resize: "vertical", minHeight: 70 }}
          />
        ) : type === "date" ? (
          <input className="fe-input" autoFocus value={val} onChange={e => setVal(e.target.value)} onKeyDown={handleKeyDown} type="date" style={inputStyle} />
        ) : type === "number" || type === "currency" ? (
          <div style={{ position: "relative" }}>
            {type === "currency" && (
              <span style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: T.tx3, fontSize: 14 }}>$</span>
            )}
            <input
              className="fe-input"
              autoFocus value={val} onChange={e => setVal(e.target.value)} onKeyDown={handleKeyDown}
              type="number"
              style={{ ...inputStyle, paddingLeft: type === "currency" ? 28 : 14 }}
            />
          </div>
        ) : type === "phone" ? (
          <input className="fe-input" autoFocus value={val} onChange={e => setVal(e.target.value)} onKeyDown={handleKeyDown} type="tel" placeholder="(555) 123-4567" style={inputStyle} />
        ) : type === "email" ? (
          <input className="fe-input" autoFocus value={val} onChange={e => setVal(e.target.value)} onKeyDown={handleKeyDown} type="email" placeholder="name@company.com" style={inputStyle} />
        ) : type === "url" ? (
          <input className="fe-input" autoFocus value={val} onChange={e => setVal(e.target.value)} onKeyDown={handleKeyDown} type="url" placeholder="https://" style={inputStyle} />
        ) : (
          <input className="fe-input" autoFocus value={val} onChange={e => setVal(e.target.value)} onKeyDown={handleKeyDown} type="text" style={inputStyle} />
        )}
      </Modal>
    </>
  );
}
